import { SOUTH_AFRICA_TIME_ZONE } from "~/utils/timezone";

type ActivityDiffField = {
  field: string;
  before: string;
  after: string;
};

export type CustomerNoteRecord = {
  id: string;
  text: string;
  urgent: boolean;
  createdAtMs: number;
  updatedAtMs: number;
};

export type CustomerTaskRecord = {
  id: string;
  title: string;
  scheduledAtMs: number;
  completed: boolean;
  createdAtMs: number;
  updatedAtMs: number;
  createdByOperatorId?: string;
  createdByOperatorName?: string;
};

export type CustomerActivityRecord = {
  id: string;
  timestampMs: number;
  message: string;
  diff?: ActivityDiffField[];
  operatorName?: string | null;
  source?: "ui" | "socket";
};

export type CustomerInteractionData = {
  notes: CustomerNoteRecord[];
  tasks: CustomerTaskRecord[];
  activity: CustomerActivityRecord[];
};

const STORAGE_KEY = "aidcall.customer-interactions.v1";
const UPDATE_EVENT = "aidcall:customer-interactions-updated";

function emptyData(): CustomerInteractionData {
  return {
    notes: [],
    tasks: [],
    activity: [],
  };
}

function canUseStorage() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function safeParse(value: string | null) {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    return parsed as Record<string, CustomerInteractionData>;
  } catch {
    return {};
  }
}

function safeWrite(value: Record<string, CustomerInteractionData>) {
  if (!canUseStorage()) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

function emitUpdated(customerId: string) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(UPDATE_EVENT, {
      detail: { customerId },
    })
  );
}

export function loadCustomerInteractions(customerId: string) {
  if (!customerId) return emptyData();
  if (!canUseStorage()) return emptyData();
  const raw = window.localStorage.getItem(STORAGE_KEY);
  const parsed = safeParse(raw);
  return parsed[customerId] ?? emptyData();
}

export function loadAllCustomerInteractions() {
  if (!canUseStorage()) return {};
  const raw = window.localStorage.getItem(STORAGE_KEY);
  return safeParse(raw);
}

export function saveCustomerInteractions(
  customerId: string,
  value: CustomerInteractionData
) {
  if (!customerId || !canUseStorage()) return;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  const parsed = safeParse(raw);
  parsed[customerId] = value;
  safeWrite(parsed);
  emitUpdated(customerId);
}

export function updateCustomerTaskCompletion(
  customerId: string,
  taskId: string,
  completed: boolean,
  options?: {
    message?: string;
    operatorName?: string | null;
    source?: CustomerActivityRecord["source"];
    timestampMs?: number;
  }
) {
  if (!customerId || !taskId) return false;
  const current = loadCustomerInteractions(customerId);
  const existingTask = current.tasks.find((task) => task.id === taskId);
  if (!existingTask) return false;
  if (existingTask.completed === completed) return false;

  const timestampMs = options?.timestampMs ?? Date.now();
  const nextTasks = current.tasks.map((task) =>
    task.id === taskId
      ? {
          ...task,
          completed,
          updatedAtMs: timestampMs,
        }
      : task
  );
  const statusBefore = existingTask.completed ? "Completed" : "Pending";
  const statusAfter = completed ? "Completed" : "Pending";
  const diff = [
    createDiffField("Task", "", existingTask.title),
    createDiffField("Status", statusBefore, statusAfter),
  ].filter(Boolean) as NonNullable<CustomerActivityRecord["diff"]>;
  const activityMessage =
    options?.message ?? (completed ? "Task completed" : "Task marked incomplete");
  saveCustomerInteractions(customerId, {
    notes: current.notes,
    tasks: nextTasks,
    activity: current.activity,
  });
  appendCustomerActivity(customerId, {
    message: activityMessage,
    diff: diff.length > 0 ? diff : undefined,
    operatorName: options?.operatorName ?? null,
    source: options?.source ?? "ui",
    timestampMs,
  });
  return true;
}

export function subscribeCustomerInteractions(
  listener: (customerId: string) => void
) {
  if (typeof window === "undefined") return () => {};
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ customerId?: string }>).detail;
    if (!detail?.customerId) return;
    listener(detail.customerId);
  };
  window.addEventListener(UPDATE_EVENT, handler as EventListener);
  return () => {
    window.removeEventListener(UPDATE_EVENT, handler as EventListener);
  };
}

export function createDiffField(
  field: string,
  before: string | number | boolean | null | undefined,
  after: string | number | boolean | null | undefined
): ActivityDiffField | null {
  const beforeValue = String(before ?? "");
  const afterValue = String(after ?? "");
  if (beforeValue === afterValue) return null;
  return {
    field,
    before: beforeValue,
    after: afterValue,
  };
}

export function appendCustomerActivity(
  customerId: string,
  value: {
    message: string;
    diff?: CustomerActivityRecord["diff"];
    operatorName?: string | null;
    source?: CustomerActivityRecord["source"];
    timestampMs?: number;
  }
) {
  if (!customerId) return null;
  const message = value.message.trim();
  if (!message) return null;

  const current = loadCustomerInteractions(customerId);
  const timestampMs = value.timestampMs ?? Date.now();
  const randomSuffix = Math.random().toString(36).slice(2, 8);
  const activity: CustomerActivityRecord = {
    id: `activity-${timestampMs}-${randomSuffix}`,
    timestampMs,
    message,
    ...(value.diff && value.diff.length > 0 ? { diff: value.diff } : {}),
    ...(value.operatorName ? { operatorName: value.operatorName } : {}),
    source: value.source ?? "ui",
  };

  saveCustomerInteractions(customerId, {
    notes: current.notes,
    tasks: current.tasks,
    activity: [activity, ...current.activity],
  });

  return activity;
}

export function validateScheduledDateAfterNow(
  scheduledAtMs: number,
  nowMs = Date.now()
) {
  return Number.isFinite(scheduledAtMs) && scheduledAtMs > nowMs;
}

export function formatActivityTimestamp(timestampMs: number) {
  const date = new Date(timestampMs);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: SOUTH_AFRICA_TIME_ZONE,
  });
}

export function formatTaskDueLabel(timestampMs: number) {
  const date = new Date(timestampMs);
  if (Number.isNaN(date.getTime())) return "Due date not set";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: SOUTH_AFRICA_TIME_ZONE,
  });
}
